import MuiInput from "@mui/material/Input";
import FormFieldError from "components-redesign/FormFieldError";
import InputLabel from "components-redesign/InputLabel";
import { FIGMA_COLORS, TYPOGRAPHY } from "components-redesign/styles";
import InputBoxes from "components/FormControls/CharacterInputBoxes/CharacterInputBoxes";
import useCodeValue from "components/FormControls/CharacterInputBoxes/useCodeValue";
import { makeStyles } from "components/mui-core";
import { Integer } from "model/modelTypes";
import React from "react";

const BOX_SIZE = 40;
const BOX_GAP = 20;

const useStyles = makeStyles()({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
  },
  boxes: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: BOX_GAP,
    height: BOX_SIZE,
  },
  box: {
    width: BOX_SIZE,
    height: BOX_SIZE,
  },
  boxInput: {
    ...TYPOGRAPHY.titleLarge,
    color: FIGMA_COLORS.onSurfaceLight,
    textAlign: "center",
    padding: 0,
  },
  boxUnderline: {
    "&:before": {
      borderBottom: `1px solid ${FIGMA_COLORS.onSurfaceLight}`,
    },
    "&:hover:not(.Mui-disabled):before": {
      borderBottom: `1px solid ${FIGMA_COLORS.onSurfaceLight}`,
    },
    "&:after": {
      borderBottom: `2px solid ${FIGMA_COLORS.primaryLight}`,
    },
    "&.Mui-error:before, &.Mui-error:hover:not(.Mui-disabled):before, &.Mui-error:after":
      {
        borderBottomColor: FIGMA_COLORS.errorLight,
      },
  },
  separator: {
    ...TYPOGRAPHY.titleLarge,
    color: FIGMA_COLORS.onSurfaceLight,
  },
  errorRow: {
    display: "flex",
    justifyContent: "center",
    textAlign: "center",
  },
});

type IProps = {
  value?: string;
  setValue: (val?: string) => void;
  length?: Integer;
  /** `true` reddens the boxes without a message, anything else prints below. */
  error?: React.ReactNode;
  label?: string;
  /** Splits the boxes into groups of this many, e.g. 123 - 456. */
  groupSize?: Integer;
  name?: string;
};

/**
 * One box per character, with the keyboard, paste and focus behaviour shared
 * with the legacy code input - only the styling is this component's own.
 */
const CodeInput = ({
  value,
  setValue,
  length = 6,
  error,
  label,
  groupSize = 3,
  name = "code",
}: IProps) => {
  const { classes } = useStyles();
  const { valueArr, setValueArr, handlePaste } = useCodeValue({
    value,
    setValue,
    length,
  });

  return (
    <div
      className={classes.root}
      onPaste={handlePaste}>
      {label ? <InputLabel error={!!error}>{label}</InputLabel> : null}
      <div className={classes.boxes}>
        <InputBoxes
          valueArr={valueArr}
          setValueArr={setValueArr}
          length={length}
          groupSize={groupSize}
          separator={<div className={classes.separator}>–</div>}
          renderBox={({ index, inputProps, boxRef, ...boxProps }) => (
            <MuiInput
              {...inputProps}
              data-qa="input-box"
              autoFocus={boxProps.autoFocus}
              error={!!error}
              className={classes.box}
              classes={{
                input: classes.boxInput,
                underline: classes.boxUnderline,
              }}
              type="text"
              onKeyDown={boxProps.handleKeyDown}
              onChange={boxProps.handleChange}
              onFocus={boxProps.handleFocus}
              name={`${index}`}
              ref={boxRef}
            />
          )}
        />
      </div>
      {error && error !== true && (
        <div className={classes.errorRow}>
          <FormFieldError
            id={name}
            text={error}
          />
        </div>
      )}
    </div>
  );
};

export default CodeInput;
